/**
 * 版本檢查工具
 * 用於檢查前端版本是否更新，並在 localStorage 中存儲版本資訊
 */
import localStorageUtil from './localstorage'

const VERSION_STORAGE_KEY = 'version_info'

export interface VersionInfo {
  version: string
  commitHash: string
  fullCommitHash: string
  branch: string
  buildTime: string
  buildTimestamp: number
}

export interface StoredVersionInfo extends VersionInfo {
  lastAccessTime: string
  lastAccessTimestamp: number
}

/**
 * 比較兩個 baseVersion（忽略 branch / commit 等資訊）
 * version 字串格式預期為：
 *   `${baseVersion} | ${gitInfo.branch} | ${gitInfo.commitHash} | ${buildTime}`
 * 若沒有 `|`，則整個字串視為 baseVersion。
 * 回傳值：
 *   > 0 代表 v1 > v2
 *   < 0 代表 v1 < v2
 *   = 0 代表相等
 */
export function compareBaseVersion(version1: string, version2: string): number {
  const normalizeBase = (v: string) => {
    if (!v) return ''

    // 先切掉後面附加的資訊，只留 baseVersion
    const basePart = v.split('|')[0].trim()

    // 移除預釋/metadata 標記，例如 1.2.3-beta+001
    return basePart.split('_')[0].split('+')[0]
  }

  const v1Parts = normalizeBase(version1)
    .split('.')
    .filter(p => p !== '')
    .map(p => {
      const n = Number(p)
      return Number.isFinite(n) ? n : 0
    })

  const v2Parts = normalizeBase(version2)
    .split('.')
    .filter(p => p !== '')
    .map(p => {
      const n = Number(p)
      return Number.isFinite(n) ? n : 0
    })

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1Part = v1Parts[i] ?? 0
    const v2Part = v2Parts[i] ?? 0

    if (v1Part > v2Part) return 1
    if (v1Part < v2Part) return -1
  }

  return 0
}

export function getStoredVersion(): StoredVersionInfo | null {
  try {
    const stored = localStorageUtil.get(VERSION_STORAGE_KEY)
    return stored ? stored : null
  } catch (error) {
    console.error('Error reading stored version:', error)
    return null
  }
}

export function storeVersion(versionInfo: VersionInfo): void {
  try {
    const storedInfo: StoredVersionInfo = {
      ...versionInfo,
      lastAccessTime: new Date().toISOString(),
      lastAccessTimestamp: Date.now(),
    }
    localStorageUtil.set(VERSION_STORAGE_KEY, storedInfo)
  } catch (error) {
    console.error('Error storing version:', error)
  }
}

/**
 * 檢查版本是否有更新
 * 比較 baseVersion ->fullCommitHash-> buildTimestamp
 * @returns { isNewVersion: boolean, previousVersion: StoredVersionInfo | null }
 */
export function checkVersionUpdate(currentVersion: VersionInfo): {
  isNewVersion: boolean
  previousVersion: StoredVersionInfo | null
} {
  const storedVersion = getStoredVersion()
  if (!storedVersion) {
    // 首次訪問，沒有舊版本資訊，僅存入當前版本
    storeVersion(currentVersion)
    return { isNewVersion: false, previousVersion: null }
  }

  let isNewVersion = false

  const compareVersion = compareBaseVersion(currentVersion.version, storedVersion.version)
  const sameCommitHash = currentVersion.fullCommitHash === storedVersion.fullCommitHash
  const isDifferentBuild = currentVersion.buildTimestamp !== storedVersion.buildTimestamp

  if (compareVersion > 0) {
    isNewVersion = true
  } else if (compareVersion < 0) {
    isNewVersion = false
  } else {
    isNewVersion = sameCommitHash && isDifferentBuild
  }

  // 轉換為 StoredVersionInfo 格式（以 localStorage 為唯一來源）
  const previousVersion: StoredVersionInfo = {
    ...storedVersion,
    lastAccessTime: storedVersion.lastAccessTime || new Date().toISOString(),
    lastAccessTimestamp: storedVersion.lastAccessTimestamp || Date.now(),
  }

  // 更新存儲的版本資訊
  storeVersion(currentVersion)

  return { isNewVersion, previousVersion }
}

export async function loadVersionInfo(): Promise<VersionInfo | null> {
  const maxRetries = 2
  const timeout = 5000 // 5 秒超時

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)

      const response = await fetch(
        `${process.env.REACT_APP_BASE_NAME || ''}/version.json?t=${Date.now()}`,
        {
          signal: controller.signal,
          cache: 'no-store', // 避免緩存
        }
      )

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (attempt < maxRetries) {
          continue // 重試
        }
        console.warn(
          `Failed to load version.json after ${attempt + 1} attempts:`,
          response.statusText
        )
        return null
      }

      const versionInfo: VersionInfo = await response.json()

      // 驗證版本資訊的完整性
      if (!versionInfo.version || !versionInfo.commitHash) {
        console.warn('Invalid version.json format')
        return null
      }

      return versionInfo
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn(`Version.json request timeout (attempt ${attempt + 1})`)
      } else if (attempt < maxRetries) {
        // 重試
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))) // 遞增延遲
        continue
      } else {
        console.error('Error loading version.json:', error)
        return null
      }
    }
  }

  return null
}

/**
 * 初始化版本檢查
 * 在應用啟動時調用此函數
 * @returns { isNewVersion: boolean, currentVersion: VersionInfo | null, previousVersion: StoredVersionInfo | null }
 */
export async function initVersionCheck(): Promise<{
  isNewVersion: boolean
  currentVersion: VersionInfo | null
  previousVersion: StoredVersionInfo | null
}> {
  const currentVersion = await loadVersionInfo()

  if (!currentVersion) {
    return {
      isNewVersion: false,
      currentVersion: null,
      previousVersion: null,
    }
  }

  const { isNewVersion, previousVersion } = checkVersionUpdate(currentVersion)

  return {
    isNewVersion,
    currentVersion,
    previousVersion,
  }
}
